import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import SubAdminServices from "../../../services/SubAdminServices";

const validationSchema = Yup.object().shape({
  wallet_balance: Yup.number()
    .typeError("Amount must be a number")
    .integer("Amount must be a whole number")
    .positive("Amount must be greater than 0")
    .required("Amount is required"),
});

/**
 * Receipt = Admin funds the sub admin (CREDIT), Payment = Admin pulls the
 * money back (DEBIT). Both legs move between the two wallets on the server.
 */
const SubAdminWalletModal = ({ show, onClose, walletUser, onSaved }) => {
  const api = new SubAdminServices();
  const [isLoading, setIsLoading] = useState(false);
  const balance = Number(walletUser?.wallet_balance) || 0;

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" keyboard={false} size="md">
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">Sub Admin Wallet — {walletUser?.name}</h5>
        <button className="btn" onClick={onClose}>
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />

      <Formik
        initialValues={{ wallet_payment: "Receipt", wallet_balance: "" }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const amount = Number(values.wallet_balance);
          const isDeduct = values.wallet_payment === "Payment";
          if (isDeduct && amount > balance) {
            toast.error(`${walletUser?.username} has only ${balance.toLocaleString("en-IN")} in wallet.`);
            setSubmitting(false);
            return;
          }
          setIsLoading(true);
          try {
            const response = await api.wallet({
              user_id: walletUser.id,
              wallet_payment: values.wallet_payment,
              wallet_balance: amount,
              type: isDeduct ? "DEBIT" : "CREDIT",
            });
            if (response.isSuccess) {
              toast.success(response.message);
              onClose();
              onSaved(walletUser.id, response.balance);
            } else {
              toast.error(response.message);
            }
          } catch (error) {
            toast.dismiss();
            toast.error(error?.response?.data?.message || "Wallet update failed");
          } finally {
            setIsLoading(false);
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          const after = balance - (Number(values.wallet_balance) || 0);
          return (
            <Form className="addAdmin-form">
              <Modal.Body className="pt-2">
                <div className="form-group mb-2">
                  <div className="form-label mb-0">
                    Payment Method <span className="required-asterisk">*</span>
                  </div>
                  <div className="input-wrapper add-wallet-payment-options">
                    <div className="add-wallet-radio-option">
                      <input
                        type="radio"
                        className="form-check-input me-1"
                        name="wallet_payment"
                        id="sa_wallet_receipt"
                        value="Receipt"
                        checked={values.wallet_payment === "Receipt"}
                        onChange={(e) => setFieldValue("wallet_payment", e.target.value)}
                      />
                      <label htmlFor="sa_wallet_receipt">Receipt (Add Balance)</label>
                    </div>
                    <div className="add-wallet-radio-option">
                      <input
                        type="radio"
                        className="form-check-input me-1"
                        name="wallet_payment"
                        id="sa_wallet_payment"
                        value="Payment"
                        checked={values.wallet_payment === "Payment"}
                        onChange={(e) => setFieldValue("wallet_payment", e.target.value)}
                      />
                      <label htmlFor="sa_wallet_payment">Payment (Deduct Balance)</label>
                    </div>
                  </div>
                </div>

                <div className="form-group mb-2">
                  <label htmlFor="wallet_balance" className="form-label mb-0">
                    Amount <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Field
                      type="number"
                      step="1"
                      min="1"
                      id="wallet_balance"
                      name="wallet_balance"
                      placeholder="Whole rupees"
                      className="form-input"
                      onKeyDown={(e) => {
                        if ([".", ",", "e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                      }}
                    />
                  </div>
                  <ErrorMessage name="wallet_balance">
                    {(msg) => <div className="admin_error-text">{msg}</div>}
                  </ErrorMessage>
                </div>

                <div className="d-flex align-items-center justify-content-between">
                  <div className={`amount-text ${values.wallet_payment === "Payment" && after < 0 ? "loss" : "profit"}`}>
                    {walletUser?.username} balance: {balance.toLocaleString("en-IN")}
                    {values.wallet_payment === "Payment" && values.wallet_balance
                      ? ` → ${after.toLocaleString("en-IN")}`
                      : ""}
                  </div>
                  <button type="submit" className="add_admin-btn" disabled={isSubmitting || isLoading}>
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Submitting...
                      </>
                    ) : values.wallet_payment === "Payment" ? (
                      "Deduct Now"
                    ) : (
                      "Add Now"
                    )}
                  </button>
                </div>
              </Modal.Body>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default SubAdminWalletModal;
